/**
 * Addresses in the new portal, and the switch's mapping between the two
 * versions: each page in one goes to the same page in the other (the same
 * client, the same chat), or to the nearest one while a page is still being
 * rebuilt.
 */
export const NEW_BASE = '/new';

export const NEW_ROUTES = {
  /** /new itself: sends the coach to Home or To-do, as they chose in Settings → Home. */
  LANDING: `${NEW_BASE}`,
  /** The calm Home: greeting, callouts and the Ask box (BEHAVIOUR.md "Home (calm) and To-do"). */
  HOME: `${NEW_BASE}/home`,
  /** To-do: the full list of what needs the coach (the page that used to be Today). */
  TODO: `${NEW_BASE}/todo`,
  /** Where Today used to be; it redirects to To-do so old links still open. */
  OLD_TODAY: `${NEW_BASE}/today`,
  CLIENTS: `${NEW_BASE}/clients`,
  CLIENT: `${NEW_BASE}/clients/:clientId`,
  /** A client's suggested weights to review (item M), ?week= for one week. */
  SUGGESTED_WEIGHTS: `${NEW_BASE}/clients/:clientId/suggested-weights`,
  CHAT: `${NEW_BASE}/chat/:clientId?`,
  TEMPLATES: `${NEW_BASE}/templates`,
  EXERCISES: `${NEW_BASE}/exercises`,
  PLAYBOOK: `${NEW_BASE}/playbook`,
  FORMS: `${NEW_BASE}/forms`,
  NUTRITION: `${NEW_BASE}/nutrition`,
  COMMUNITY: `${NEW_BASE}/community`,
  TEAM: `${NEW_BASE}/team`,
  CLIENT_EMAILS: `${NEW_BASE}/client-emails/:key?`,
  SETTINGS: `${NEW_BASE}/settings`,
  CLASSROOM: `${NEW_BASE}/classroom`,
  /** The hub of everything that isn't in the sidebar (Templates, Exercises, Playbook, Forms, Nutrition, Team, Settings…). */
  LIBRARY: `${NEW_BASE}/library`,
};

const CLASSIC = '/trainer';

/** Where a page of the current portal lives in the new one. */
export const toNewPath = (pathname: string, search = ''): string => {
  const path = pathname.replace(/\/+$/, '');
  const program = path.match(/^\/trainer\/trainers-clients\/([^/]+)\/phases\/(\d+)/);
  if (program) {
    return `${NEW_BASE}/clients/${program[1]}/program/${program[2]}`;
  }
  const client = path.match(/^\/trainer\/trainers-clients\/([^/]+)/);
  if (client) {
    return `${NEW_BASE}/clients/${client[1]}`;
  }
  if (path === `${CLASSIC}/chat-v2` || path === `${CLASSIC}/chat`) {
    const chatClient = new URLSearchParams(search).get('client');
    return chatClient ? `${NEW_BASE}/chat/${chatClient}` : `${NEW_BASE}/chat`;
  }
  const pages: Record<string, string> = {
    [`${CLASSIC}/trainers-clients`]: `${NEW_BASE}/clients`,
    [`${CLASSIC}/my-exercises`]: `${NEW_BASE}/exercises`,
    [`${CLASSIC}/admin-exercises`]: `${NEW_BASE}/exercises`,
    [`${CLASSIC}/templates`]: `${NEW_BASE}/templates`,
    [`${CLASSIC}/cues`]: `${NEW_BASE}/playbook`,
    [`${CLASSIC}/forms`]: `${NEW_BASE}/forms`,
    [`${CLASSIC}/nutrition`]: `${NEW_BASE}/nutrition`,
    [`${CLASSIC}/team`]: `${NEW_BASE}/team`,
  };
  return pages[path] || NEW_BASE;
};

/** Where a page of the new portal lives in the current one. */
export const toClassicPath = (pathname: string): string => {
  const path = pathname.replace(/\/+$/, '');
  const program = path.match(/^\/new\/clients\/([^/]+)\/program\/(\d+)/);
  if (program) {
    return `${CLASSIC}/trainers-clients/${program[1]}/phases/${program[2]}`;
  }
  const client = path.match(/^\/new\/clients\/([^/]+)/);
  if (client) {
    return `${CLASSIC}/trainers-clients/${client[1]}`;
  }
  const chat = path.match(/^\/new\/chat\/([^/]+)/);
  if (chat) {
    return `${CLASSIC}/chat-v2?client=${chat[1]}`;
  }
  const pages: Record<string, string> = {
    [`${NEW_BASE}/clients`]: `${CLASSIC}/trainers-clients`,
    [`${NEW_BASE}/chat`]: `${CLASSIC}/chat`,
    [`${NEW_BASE}/exercises`]: `${CLASSIC}/my-exercises`,
    [`${NEW_BASE}/templates`]: `${CLASSIC}/templates`,
    [`${NEW_BASE}/playbook`]: `${CLASSIC}/cues`,
    [`${NEW_BASE}/forms`]: `${CLASSIC}/forms`,
    [`${NEW_BASE}/nutrition`]: `${CLASSIC}/nutrition`,
    [`${NEW_BASE}/team`]: `${CLASSIC}/team`,
  };
  // A page inside a section (a form, a client's nutrition) goes to that section.
  const section = path.match(/^\/new\/(exercises|playbook|forms|nutrition|templates)\//);
  return pages[path] || (section && pages[`${NEW_BASE}/${section[1]}`]) || `${CLASSIC}/trainers-clients`;
};
